import { Navigate, useLocation } from "react-router-dom";
import Main from "../pages/Main";
import { getSubsistema, useAuth } from "../hooks/AuthenticateContext";
import { useEffect, useState } from "react";
import SkeletonLoading from "../components/SkeletonLoading";
import { MenuItem, carrierMenuItems, isCarrierUser, menuItems } from "../utils/menuItems";

interface Props {
  children: JSX.Element;
}

const PrivateRoute = (props: Props) => {
  const { verifySession, isLoadingPermissions, user } = useAuth();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(true);

  const token = localStorage.getItem("token") || localStorage.getItem("token");
  const urlParams = new URLSearchParams(window.location.search);
  const tokenUrl = urlParams.get("token");

  useEffect(() => {
    let isMounted = true;

    const checkSession = async () => {
      if (!token && !tokenUrl) {
        setHasValidSession(false);
        setIsVerifying(false);
        return;
      }

      const isValid = await verifySession();
      if (isMounted) {
        setHasValidSession(isValid);
        setIsVerifying(false);
      }
    };

    checkSession();

    return () => {
      isMounted = false;
    };
  }, [verifySession, location.pathname, token, tokenUrl]);

  if (!token && !tokenUrl) {
    return <Navigate to="/" replace />;
  }

  if (isVerifying || isLoadingPermissions) {
    return (
      <Main>
        <div className="p-4">
          <SkeletonLoading type="table" showHeader={false} rows={8} />
        </div>
      </Main>
    );
  }

  if (!hasValidSession) {
    return <Navigate to="/" replace />;
  }

  // Se for administrador, ignora a validação de permissão de rotas e acessa tudo
  if (user?.grupo === "ADMINISTRADOR") {
    return <Main>{props.children}</Main>;
  }

  // Carrega as permissões do localStorage
  const storedPermissions = localStorage.getItem("permissions") || localStorage.getItem("permissions");
  const permissions: any[] = storedPermissions ? JSON.parse(storedPermissions) : [];

  // Chaves de permissão (ex.: "carrossel") são compartilhadas entre o menu do
  // pátio e o da transportadora; só valem as permissões do subsistema do login
  // (linhas sem subsistema definido continuam valendo).
  const subsistemaAtual = getSubsistema(user);

  const hasPermission = (permissionKey?: string) => {
    if (!permissionKey) return true;
    return permissions.some((p: any) =>
      (p.submodule === permissionKey || p.module === permissionKey) &&
      p.action === 'ACESSAR' &&
      (!p.subsistema || p.subsistema === subsistemaAtual)
    );
  };

  const getAccessibleRoutes = (items: MenuItem[]) => {
    const routes: string[] = [];
    items.forEach(item => {
      if (item.path) {
        if (!item.permission || hasPermission(item.permission)) {
          routes.push(item.path);
        }
      }
      if (item.submenu) {
        item.submenu.forEach(subItem => {
          if (subItem.path) {
            if (!subItem.permission || hasPermission(subItem.permission)) {
              routes.push(subItem.path);
            }
          }
        });
      }
    });
    return routes;
  };

  const isCarrier = isCarrierUser(user);
  const items = isCarrier ? carrierMenuItems : menuItems;

  // Usuário de transportadora só acessa o subsistema /transportadora, mesmo
  // digitando a URL de uma tela do pátio diretamente.
  if (isCarrier && !location.pathname.startsWith('/transportadora')) {
    const allowedRoutes = getAccessibleRoutes(items);
    return <Navigate to={allowedRoutes[0] ?? "/"} replace />;
  }

  // Busca se a rota atual exige alguma permissão definida nos menus
  let requiredPermission: string | undefined;
  let isMenuItem = false;

  for (const item of items) {
    if (item.path === location.pathname) {
      requiredPermission = item.permission;
      isMenuItem = true;
      break;
    }
    if (item.submenu) {
      const sub = item.submenu.find(s => s.path === location.pathname);
      if (sub) {
        requiredPermission = sub.permission;
        isMenuItem = true;
        break;
      }
    }
  }

  // Se a rota atual está no menu e o usuário NÃO tem permissão, redireciona para a primeira rota disponível
  if (isMenuItem && !hasPermission(requiredPermission)) {
    const allowedRoutes = getAccessibleRoutes(items);
    if (allowedRoutes.length > 0) {
      return <Navigate to={allowedRoutes[0]} replace />;
    }
    // Sem nenhuma tela liberada não dá para mandar para "/": o login vê a
    // sessão válida e devolve para cá, gerando um loop de redirecionamento.
    return (
      <Main>
        <div className="p-8 text-center text-gray-600">
          Seu grupo não possui permissão de acesso a nenhuma tela. Procure o administrador do sistema.
        </div>
      </Main>
    );
  }

  return <Main>{props.children}</Main>;
};

export default PrivateRoute;
